import { FormFieldValueGroupUI } from "Backend";
import { STARRED, UNTAGGED } from "components/tags/constants";
import { anyDateInRange, groupValueGroupByCellId, valueGroupDateValues } from "components/FormField/form-field-helpers";
import { FormFieldContext, GroupedValueGroup } from "contexts/FormFieldContext";
import { FieldValueGroupSelectionStatus, TagView } from "generated/graphql";
import { Dispatch, SetStateAction, useContext, useMemo } from "react";

export interface FilteredValueGroupData {
  filteredValueGroups: FormFieldValueGroupUI[];
  filteredValueGroupsWithReferences: FormFieldValueGroupUI[];
  groupedAndFilteredValueGroups: GroupedValueGroup[];
  groupedAndFilteredValueGroupsWithReferences: GroupedValueGroup[];
  availableTags: (TagView & { count: number })[];
  tagFilters: Set<string>;
  setTagFilters: Dispatch<SetStateAction<Set<string>>>;
  tagCounts: { [tag: string]: number };
}

export function useFilteredValueGroups(): FilteredValueGroupData {
  const ffContext = useContext(FormFieldContext);
  const [tagFilters, setTagFilters] = ffContext.tagHandle;

  const baseValueGroups = ffContext.valueGroups;

  const [filteredValueGroups, tagCounts]: [FormFieldValueGroupUI[], { [tag: string]: number }] = useMemo(() => {
    type ValueGroupPredicate = (vg: FormFieldValueGroupUI) => boolean;
    const predicates: ValueGroupPredicate[] = Array.from(tagFilters).map((t) => {
      if (t === STARRED) return (vg) => vg.selectionStatus === FieldValueGroupSelectionStatus.Selected;
      else if (t === UNTAGGED) return (vg) => vg.tags.length === 0;
      else return (vg) => vg.tags.some((appliedTag) => t === appliedTag.systemName);
    });
    // The new response card will have this sentinel id
    predicates.push((vg) => vg.serialId === -1);
    const tagCounts: { [tag: string]: number } = {};
    const filteredValueGroups: FormFieldValueGroupUI[] = [];

    baseValueGroups.forEach((vg) => {
      if (predicates.some((f) => f(vg))) {
        filteredValueGroups.push(vg);
      }
      if (vg.selectionStatus === FieldValueGroupSelectionStatus.Selected)
        tagCounts[STARRED] = (tagCounts[STARRED] ?? 0) + 1;
      if (vg.tags.length === 0) tagCounts[UNTAGGED] = (tagCounts[UNTAGGED] ?? 0) + 1;
      vg.tags.forEach((t) => (tagCounts[t.systemName] = (tagCounts[t.systemName] ?? 0) + 1));
    });
    return [predicates.length === 1 ? baseValueGroups : filteredValueGroups, tagCounts];
  }, [baseValueGroups, tagFilters]);

  const availableTags = Array.from(new Set(baseValueGroups.length > 0 ? baseValueGroups[0]?.availableTags : [])).map(
    (tagView) => ({ ...tagView, count: tagCounts[tagView.systemName] ?? 0 })
  );

  const filteredValueGroupsWithReferences = filteredValueGroups.filter((vg) => vg.references.length > 0);

  const valueGroupsInRange: FormFieldValueGroupUI[] = useMemo(() => {
    const startDate = ffContext.groupedValueGroupStartDateHandle[0];
    const endDate = ffContext.groupedValueGroupEndDateHandle[0];

    return filteredValueGroups.filter((vg) => {
      const dates = valueGroupDateValues(vg);
      return dates.length > 0 ? anyDateInRange(dates, startDate, endDate) : true;
    });
  }, [filteredValueGroups, ffContext.groupedValueGroupStartDateHandle[0], ffContext.groupedValueGroupEndDateHandle[0]]);

  const groupedValueGroup: GroupedValueGroup[] = useMemo(() => {
    const groupBy = ffContext.groupByCell?.id;
    // It's possible that valueGroups and the formField are out of sync
    if (groupBy === undefined || valueGroupsInRange.some((vg) => vg.fieldId !== ffContext.formField.id)) {
      return [];
    }
    return Object.values(groupValueGroupByCellId(valueGroupsInRange, groupBy, ffContext.cellByIdMap));
  }, [valueGroupsInRange, ffContext.groupByCell?.id, ffContext.cellByIdMap, ffContext.formField.id]);

  const hasAnyReferences = (vg: FormFieldValueGroupUI) => vg.references.length > 0;
  const groupedAndFilteredValueGroupsWithReferences = groupedValueGroup
    .map((group) => ({ ...group, valueGroups: group.valueGroups.filter(hasAnyReferences) }))
    .filter((group) => group.valueGroups.some(hasAnyReferences));

  return {
    availableTags,
    tagFilters,
    setTagFilters,
    filteredValueGroups,
    filteredValueGroupsWithReferences,
    groupedAndFilteredValueGroups: groupedValueGroup,
    groupedAndFilteredValueGroupsWithReferences,
    tagCounts,
  };
}
